<template>
  <v-card :style="listView ? 'box-shadow: none;' : ''">
    <div v-if="!listView">
      <SelectedCourse :appId="appId" :routeName="changeScopeRouteName" />
    </div>
    <v-card-title>
      <v-card flat class="d-flex" width="100%" v-if="!listView">
        <span>{{ $t("Subjects") }}</span>
        <v-spacer />
        <v-btn
          class="success"
          v-if="haveAddPermisions"
          @click="openAddSubject"
        >{{ $t("buttons.add") }}</v-btn
        >
      </v-card>
      <v-card flat class="d-flex px-5 py-5" width="100%">
        <v-row>
          <v-spacer class="d-lg-block d-none" />
          <v-col lg="6">
            <v-text-field
              v-model.trim="search"
              append-icon="mdi-magnify"
              :label="`${$t('Search_by')} ${$t('Title')}`"
              single-line
              hide-details
            />
          </v-col>
        </v-row>
      </v-card>
    </v-card-title>

    <v-card-text v-if="!error">
      <v-row class="justify-center">
        <v-progress-circular
          indeterminate
          color="secondary"
          v-if="loading"
          :value="20"
        />
      </v-row>
      <v-expansion-panels
        v-if="filteredSubjects.length"
        popout
        focusable
        v-model="currentPanel"
      >
        <v-expansion-panel
          class="my-1"
          v-for="(subject, i) in filteredSubjects"
          :key="subject.id"
          @click="listView ? '' : openPanel(i, subject.id)"
        >
          <v-expansion-panel-title
            :id="`subject-${subject.id}`"
            class="text-subtitle-2"
            disable-icon-rotate
          >
            {{ subject.id }} ) {{ subject.name }}
            <template v-slot:actions>
              <v-icon
                v-if="subject.active"
                color="success"
                :title="$t('labels.active')"
              >check</v-icon
              >
              <v-icon
                v-else
                color="grey"
                :title="$t('labels.inactive')"
              >mdi-alert-circle</v-icon
              >
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-row no-gutters class="flex-sm-row flex-column" v-if="!listView">
              <v-col>
                <span
                  class="text-subtitle-1 font-weight-bold ma-1 secondary--text"
                >{{ $t("Title") }} :</span
                >
                <span class="text-subtitle-2">{{ subject.name }}</span>
              </v-col>

              <v-col>
                <span
                  class="text-subtitle-1 font-weight-bold ma-1 secondary--text"
                >{{ $t("Active") }} :</span
                >
                <span class="text-subtitle-2">{{
                  subject.active ? $t("Yes") : $t("No")
                }}</span>
              </v-col>

              <v-col>
                <span
                  class="text-subtitle-1 font-weight-bold ma-1 secondary--text"
                >{{ $t("lectureCount") }} :</span
                >
                <span class="text-subtitle-2">{{ subject.lectureCount }}</span>
              </v-col>
            </v-row>

            <div class="ilos-tree-wrapper">
              <h3>{{$t("ILOS")}}</h3>
              <div v-if="isFlexibleILOs" class="word-break-all">
                <v-treeview
                  :items="removeEmptyChildren(subject.ilos as TreeView[])"
                  class="wrappable-tree"
                  item-title="name"
                  item-value="id"
                  open-all
                >
                  <template v-slot:prepend="{ item }"><span class="d-inline-block ltr">{{ item.ilo_code }}</span>  )
                  </template>
                </v-treeview>
              </div>
              <div v-else>
                <Ilos :ilos="subject.ilos as HeaderILos" />
              </div>
            </div>

            <div class="d-flex justify-end mt-5">
              <v-btn
                v-if="haveEditPermisions && subject.id"
                small
                class="mx-2"
                color="primary"
                @click="openEditPage(subject.id)"
              >{{ $t("btn_edit") }}</v-btn
              >

              <v-btn
                v-if="haveDeletePermisions && subject.id"
                small
                class="mx-2"
                color="error"
                @click="showDeleteConfirm(subject.id)"
              >{{ $t("buttons.delete") }}</v-btn
              >
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-alert v-else-if="!loading" color="info" class="text-center">{{
        $t("No_subjects")
      }}</v-alert>
    </v-card-text>
    <v-card-text v-else>
      <v-alert color="error" class="text-center">{{$utils.servertranslate(error)}}</v-alert>
    </v-card-text>

    <v-card-actions v-if="!listView">
      <v-spacer />
      <v-btn class="blue-grey white--text" @click="goCoursesList">{{
        $t("buttons.back")
      }}</v-btn>
    </v-card-actions>

    <DialogComponent v-model="showDeleteDialog">
      <template v-slot:header>{{ $t("titles.confirmMessage") }}</template>

      <template v-slot:body>
        <span class="font-weight-bold">{{ $t("messages.sureDelete") }}</span>
      </template>

      <template v-slot:actions>
        <v-btn
          class="primary mx-1 white--text"
          :loading="loadingDelete"
          :disabled="loadingDelete"
          @click="deleteSubject()"
        >
          {{ $t("buttons.confirm") }}
        </v-btn>

        <v-btn class="secondary white--text" @click="showDeleteDialog = false">
          {{ $t("buttons.cancel") }}
        </v-btn>
      </template>
    </DialogComponent>
  </v-card>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useGoTo } from 'vuetify';
import ScopeComponent from '@/components/scope.component.vue';
import DialogComponent from '@/components/dialog.component.vue';
import IlosComponet from '@/shared/ilos-view-component.vue';
import SubjectProvider from '@/providers/subject.provider';
import { Subject, SubjectListResponse } from '@/types/subject.types';
import { HeaderILos } from '@/types/ilos.type';
import { useToaster } from '@/composables/useToaster';
import { TreeView } from '@/types/course.type';
import { SUBJECT_ROUTE_NAME, Rights, APPIDS } from '../../enums';
import { Lang } from '../../types/general.type';
import SelectedCourse from '../SelectedCourse.component.vue';

export default defineComponent({
  name: 'SubjectsList',
  components: {
    Ilos: IlosComponet,
    SelectedCourse,
    ScopeComponent,
    DialogComponent,
  },
  props: {
    listView: {
      type: Boolean,
      default: false,
    },
    showActiveOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { errorToaster } = useToaster();
    const router = useRouter();
    const route = useRoute();
    const goTo = useGoTo();

    // State
    const appId = APPIDS.SUBJECTS;
    const changeScopeRouteName = SUBJECT_ROUTE_NAME.COURSE_LIST;
    const courseId = route.params.courseId as string;

    const subjects = ref<Subject[]>([]);
    const search = ref('');
    const currentPanel = ref(-1);
    const rights = ref<string[]>([]);
    const loading = ref(false);
    const error = ref<Lang | null>(null);
    const isFlexibleILOs = ref(false);

    const showDeleteDialog = ref(false);
    const loadingDelete = ref(false);
    const toBeDeletedSubject = ref('');

    const listView = computed(() => props.listView ?? false);
    const showActiveOnly = computed(() => props.showActiveOnly ?? false);

    // Computed
    const filteredSubjects = computed(() => {
      let res = subjects.value;
      if (showActiveOnly.value) {
        res = res.filter((s) => s.active);
      }
      if (search.value) {
        const re = new RegExp(search.value, 'i');
        res = res.filter((s) => s.name?.match(re));
      }
      return res;
    });

    const haveEditPermisions = computed(() => rights.value.includes(Rights.edit)) && !listView.value;
    const haveAddPermisions = computed(() => rights.value.includes(Rights.add)) && !listView.value;
    const haveDeletePermisions = computed(() => rights.value.includes(Rights.delete)) && !listView.value;

    // Methods
    const loadCourseSubjects = async () => {
      try {
        loading.value = true;
        const response: SubjectListResponse = await SubjectProvider.getCourseSubjects(courseId);
        subjects.value = response.subjects;
        isFlexibleILOs.value = !!response.isFlexibleILOs;
        rights.value = response.rights;
        loading.value = false;
      } catch (err) {
        loading.value = false;
        error.value = err as Lang;
      }
    };

    const setCurrentPanel = (subjectId: string | undefined) => {
      const index = subjects.value.findIndex((s) => s.id === subjectId);
      if (index !== -1) {
        currentPanel.value = index;
        if (subjectId) goTo(`#subject-${subjectId}`, { duration: 300, easing: 'easeInOutCubic' });
      }
    };

    const openPanel = (index: number, subjectId: string | undefined) => {
      if (currentPanel.value !== index) {
        router.replace(`#${subjectId}`);
        setTimeout(() => {
          if (subjectId) goTo(`#subject-${subjectId}`, { duration: 300, easing: 'easeInOutCubic' });
        }, 300);
      }
    };

    const openEditPage = (subjectId: string) => {
      router.push({ name: SUBJECT_ROUTE_NAME.EDIT, params: { subjectId } });
    };

    const showDeleteConfirm = (subjectId: string) => {
      toBeDeletedSubject.value = subjectId;
      showDeleteDialog.value = true;
    };

    const deleteSubject = async () => {
      try {
        loadingDelete.value = true;
        await SubjectProvider.deleteSubject(appId, toBeDeletedSubject.value, courseId);
        loadingDelete.value = false;
        showDeleteDialog.value = false;
        toBeDeletedSubject.value = '';
        router.replace(route.path);
        router.go(0);
      } catch (err) {
        loadingDelete.value = false;
        showDeleteDialog.value = false;
        errorToaster(err as Lang, { duration: 5000 });
      }
    };

    const openAddSubject = () => router.push({ name: SUBJECT_ROUTE_NAME.ADD });

    const goCoursesList = () => {
      const { courseFilters } = (router.options.history.state as any)?.filters || {};
      router.push({ name: changeScopeRouteName, query: courseFilters });
    };

    const removeEmptyChildren = (obj?:TreeView|TreeView[]) => {
      if (!obj) return [];
      if (Array.isArray(obj)) {
        return obj.map(removeEmptyChildren);
      }
      const { children, ...rest } = obj;
      if (children?.length) {
        return {
          ...rest,
          children: removeEmptyChildren(children),
        };
      }
      return rest;
    };

    onMounted(async () => {
      await loadCourseSubjects();
      if (!listView.value) {
        if (route.hash.split('#')[1]) {
          setCurrentPanel(route.hash.split('#')[1]);
        } else if (subjects.value.length) {
          openPanel(0, subjects.value[0].id);
          setCurrentPanel(subjects.value[0].id);
        }
      }
    });

    return {
      appId,
      changeScopeRouteName,
      subjects,
      search,
      filteredSubjects,
      currentPanel,
      loading,
      error,
      isFlexibleILOs,
      haveEditPermisions,
      haveAddPermisions,
      haveDeletePermisions,
      showDeleteDialog,
      loadingDelete,
      toBeDeletedSubject,
      openPanel,
      openEditPage,
      showDeleteConfirm,
      deleteSubject,
      openAddSubject,
      goCoursesList,
      setCurrentPanel,
      removeEmptyChildren,
    };
  },
});
</script>

<style scoped>
.word-break-all {
  word-break: break-all;
}

.ilos-tree-wrapper {
  margin: 20px 0px;
}

.ilos-tree-wrapper h3 {
  margin: 0px 5px;
  text-decoration: underline;
}

:deep(.ilos-tree-wrapper .v-treeview-node__toggle + .v-treeview-node__content) {
  margin: 0px 30px;
}

:deep(.ilos-tree-wrapper .v-treeview-node__toggle) {
  display: none;
}

:deep(.wrappable-tree .v-treeview-node__label) {
  padding-top: 0px;
}

</style>
